import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { ArrowLeft, Package, Clock, CheckCircle, Truck, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/common/BottomNav';

// Define the query directly in this file
// Fixed: Added id to product for Apollo cache
const GET_USER_PURCHASES = gql`
  query GetUserPurchases($status: String) {
    myPurchases(status: $status) {
      id
      status
      totalAmount
      createdAt
      products {
        quantity
        priceAtPurchase
        product {
          id
          name
          currentPrice
          basePrice
        }
      }
      logistics {
        type
      }
    }
  }
`;

const OrdersPage = () => {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  
  const { loading, error, data } = useQuery(GET_USER_PURCHASES, {
    variables: selectedStatus !== 'ALL' ? { status: selectedStatus } : {},
    errorPolicy: 'all',
    fetchPolicy: 'network-only', // Always fetch fresh data
    onError: (error) => {
      console.error('Error fetching purchases:', error);
    }
  });

  // Calculate total from products if totalAmount is 0
  const calculateTotal = (purchase) => {
    if (purchase?.totalAmount && purchase.totalAmount > 0) {
      return purchase.totalAmount;
    }
    // Fallback: calculate from products
    return purchase?.products?.reduce((sum, item) => {
      const price = item.priceAtPurchase || item.product?.currentPrice || item.product?.basePrice || 0;
      return sum + (price * item.quantity);
    }, 0) || 0;
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <Clock size={16} className="text-yellow-600" />;
      case 'CONFIRMED':
        return <CheckCircle size={16} className="text-blue-600" />;
      case 'PROCESSING':
        return <Package size={16} className="text-purple-600" />;
      case 'SHIPPED':
        return <Truck size={16} className="text-blue-600" />;
      case 'DELIVERED':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'CANCELLED':
        return <div className="w-4 h-4 bg-red-600 rounded-full" />;
      default:
        return <Clock size={16} className="text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-50';
      case 'CONFIRMED':
        return 'text-blue-600 bg-blue-50';
      case 'PROCESSING':
        return 'text-purple-600 bg-purple-50';
      case 'SHIPPED':
        return 'text-blue-600 bg-blue-50';
      case 'DELIVERED':
        return 'text-green-600 bg-green-50';
      case 'CANCELLED':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Handle both timestamp strings and ISO date strings
    const timestamp = Number(dateString);
    const date = isNaN(timestamp) ? new Date(dateString) : new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const statusFilters = [
    { label: 'All Orders', value: 'ALL' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Confirmed', value: 'CONFIRMED' },
    { label: 'In Progress', value: 'PROCESSING' },
    { label: 'Delivered', value: 'DELIVERED' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold mb-2">Unable to Load Orders</h2>
          <p className="text-gray-600 mb-4">Please check your connection and try again</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const purchases = data?.myPurchases || [];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-4 py-4 flex items-center">
          <button 
            onClick={handleGoBack}
            className="mr-3 p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-semibold">My Orders</h1>
            <p className="text-sm text-gray-600">{purchases.length} orders</p>
          </div>
        </div>
      </div>

      {/* Status Filter */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex space-x-2 overflow-x-auto">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setSelectedStatus(filter.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                selectedStatus === filter.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Orders List */}
      <div className="max-w-lg mx-auto p-4 space-y-4">
        {purchases.length === 0 ? (
          <div className="text-center py-12">
            <Package size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No orders found</h3>
            <p className="text-gray-500 mb-6">
              {selectedStatus === 'ALL' 
                ? "You haven't placed any orders yet" 
                : `No ${selectedStatus.toLowerCase()} orders found`
              }
            </p>
            <button 
              onClick={() => navigate('/home')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          purchases.map((purchase) => (
            <div key={purchase.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Order Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {getStatusIcon(purchase.status)}
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(purchase.status)}`}>
                      {purchase.status.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">
                    {formatDate(purchase.createdAt)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">${calculateTotal(purchase).toFixed(2)}</p>
                    {purchase.savingsAmount > 0 && (
                      <p className="text-sm text-green-600">
                        Saved ${purchase.savingsAmount.toFixed(2)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">
                      Order #{purchase.id.slice(-6).toUpperCase()}
                    </p>
                    {purchase.group && (
                      <p className="text-xs text-gray-500">
                        {purchase.group.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="p-4">
                <div className="space-y-3">
                  {purchase.products?.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
                        {item.product?.images && item.product.images[0] ? (
                          <img 
                            src={item.product.images[0]} 
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <Package size={16} />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {item.product?.name || 'Product'}
                        </p>
                        <p className="text-sm text-gray-600">
                          Qty: {item.quantity} × ${(item.priceAtPurchase || item.product?.currentPrice || item.product?.basePrice || 0).toFixed(2)}
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-medium">${((item.priceAtPurchase || item.product?.currentPrice || item.product?.basePrice || 0) * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Delivery Information */}
                {purchase.logistics && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-start space-x-2">
                      <Truck size={16} className="text-gray-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">
                          {purchase.logistics.type === 'PICKUP' ? 'Pickup' : 'Delivery'}
                        </p>
                        {purchase.logistics.scheduledTime && (
                          <p className="text-gray-600">
                            {formatDate(purchase.logistics.scheduledTime)}
                          </p>
                        )}
                        {purchase.logistics.deliveryAddress && (
                          <p className="text-gray-600">
                            {purchase.logistics.deliveryAddress.address}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex space-x-2">
                  <button 
                    onClick={() => navigate(`/order-confirmation/${purchase.id}`)}
                    className="flex-1 bg-blue-50 text-blue-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                  >
                    View Details
                  </button>
                  
                  {purchase.status === 'DELIVERED' && (
                    <button className="bg-yellow-50 text-yellow-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-yellow-100 transition-colors flex items-center">
                      <Star size={14} className="mr-1" />
                      Rate
                    </button>
                  )}
                  
                  {(purchase.status === 'PENDING' || purchase.status === 'CONFIRMED') && (
                    <button className="bg-gray-50 text-gray-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default OrdersPage;